import { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, X, Camera,
  Loader2, ArrowLeft, History, Link2, Sparkles, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkUploadPage() {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const tt = (en, es) => (language === 'es' ? es : en);

  const [file, setFile] = useState(null);
  const [gsUrl, setGsUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [jobs, setJobs] = useState([]);
  const fileInputRef = useRef(null);

  // Access guard — show friendly upgrade prompt for free agents / regular users
  const canAccess = user && (
    user.role === 'admin' ||
    user.role === 'superadmin' ||
    (user.role === 'agent' && ['independent', 'pro'].includes(user.agent_tier))
  );

  const loadJobs = useCallback(async () => {
    if (!token || !canAccess) return;
    try {
      const { data } = await axios.get(`${API}/bulk-upload/jobs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobs(data.jobs || []);
    } catch (e) {
      // silent — not critical
    }
  }, [token, canAccess]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  if (!canAccess) {
    return (
      <div className="container mx-auto max-w-3xl py-10 px-4" data-testid="bulk-upload-locked">
        <Card className="border-2 border-[#D4AF37]/40 bg-amber-50/60">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-[#D4AF37] rounded-2xl flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-[#0B192C]" />
            </div>
            <h1 className="font-heading text-2xl text-[#0B192C]">
              {tt('Bulk Upload — Independent & Pro only', 'Carga Masiva — Solo Independiente y Pro')}
            </h1>
            <p className="text-slate-600 max-w-xl mx-auto">
              {tt(
                'Mass-import properties from XLSX, CSV, or Google Sheets. Available on the Independent and Pro tiers.',
                'Importa propiedades en masa desde XLSX, CSV o Google Sheets. Disponible en planes Independiente y Pro.'
              )}
            </p>
            <Link to="/upgrade">
              <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="bulk-upgrade-cta">
                {tt('Upgrade Plan →', 'Mejorar Plan →')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/bulk-upload/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'wakacasa-properties-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(tt('Template downloaded', 'Plantilla descargada'));
    } catch (e) {
      toast.error(tt('Could not download template', 'No se pudo descargar la plantilla'));
    }
  };

  const handleFileSelect = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error(tt('File too large (max 5 MB)', 'Archivo muy grande (máx 5 MB)'));
      return;
    }
    setFile(f);
    setPreview(null);
    setCommitResult(null);
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error(tt('Pick a file first', 'Selecciona un archivo primero'));
      return;
    }
    setPreviewing(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/bulk-upload/preview`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
      setCommitResult(null);
      toast.success(tt(`${data.valid} valid, ${data.errors} with errors`, `${data.valid} válidas, ${data.errors} con errores`));
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Preview failed', 'Vista previa falló'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleGsheetPreview = async () => {
    if (!gsUrl) {
      toast.error(tt('Paste a Google Sheets URL', 'Pega una URL de Google Sheets'));
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/bulk-upload/from-google-sheet`, { url: gsUrl }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(data);
      setFile(null);
      setCommitResult(null);
      toast.success(tt(`${data.valid} valid, ${data.errors} with errors`, `${data.valid} válidas, ${data.errors} con errores`));
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Sheet fetch failed', 'Lectura de hoja falló'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommit = async () => {
    if (!file) {
      toast.error(tt('Re-upload the file to commit', 'Vuelve a subir el archivo para confirmar'));
      return;
    }
    if (!preview || preview.valid === 0) {
      toast.error(tt('Nothing valid to commit', 'Nada válido para confirmar'));
      return;
    }
    setCommitting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/bulk-upload/commit`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setCommitResult(data);
      setPreview(null);
      loadJobs();
      toast.success(tt(`Created ${data.created}, updated ${data.updated}`, `Creadas ${data.created}, actualizadas ${data.updated}`));
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Commit failed', 'Confirmación falló'));
    } finally {
      setCommitting(false);
    }
  };

  const handlePhotoUpload = async (propertyId, files) => {
    if (!files || files.length === 0) return;
    const form = new FormData();
    Array.from(files).slice(0, 10).forEach(f => form.append('files', f));
    try {
      toast.loading(tt('Uploading photos…', 'Subiendo fotos…'), { id: `up-${propertyId}` });
      const { data } = await axios.post(`${API}/properties/${propertyId}/photos`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(tt(`${data.uploaded.length} photos uploaded`, `${data.uploaded.length} fotos subidas`), { id: `up-${propertyId}` });
      // Mark row as photo-uploaded for visual feedback
      setCommitResult(prev => prev && ({
        ...prev,
        rows: prev.rows.map(r => r.id === propertyId
          ? { ...r, photos_count: (data.gallery || []).length, photos_uploaded: true }
          : r
        ),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Photo upload failed', 'Carga de fotos falló'), { id: `up-${propertyId}` });
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // ===== Render =====
  const displayRows = (() => {
    const rows = (commitResult?.rows || preview?.rows || []);
    return showOnlyErrors ? rows.filter(r => !r.ok) : rows;
  })();

  return (
    <div className="container mx-auto max-w-7xl py-6 px-4 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-slate-500 hover:text-[#0B192C] flex items-center gap-1 mb-2"
            data-testid="bulk-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />{tt('Back', 'Volver')}
          </button>
          <h1 className="font-heading text-3xl text-[#0B192C] flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7 text-[#D4AF37]" />
            {tt('Bulk Property Upload', 'Carga Masiva de Propiedades')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {tt('Import up to 50 properties from XLSX, CSV or Google Sheets.', 'Importa hasta 50 propiedades desde XLSX, CSV o Google Sheets.')}
          </p>
        </div>
        <Button
          onClick={handleDownloadTemplate}
          variant="outline"
          className="border-[#D4AF37] text-[#0B192C] gap-2"
          data-testid="bulk-download-template-btn"
        >
          <Download className="w-4 h-4" />{tt('Download Template', 'Descargar Plantilla')}
        </Button>
      </div>

      {/* Upload zone (hidden after preview/commit) */}
      {!preview && !commitResult && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* File drop zone */}
          <Card
            className="border-2 border-dashed border-slate-300 hover:border-[#D4AF37] transition cursor-pointer"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            data-testid="bulk-dropzone"
          >
            <CardContent className="p-8 text-center space-y-3">
              <Upload className="w-10 h-10 mx-auto text-[#D4AF37]" />
              <p className="font-semibold text-[#0B192C]">{tt('Drop XLSX or CSV here', 'Suelta XLSX o CSV aquí')}</p>
              <p className="text-xs text-slate-500">{tt('or click to browse · max 5 MB · 50 rows', 'o haz clic para buscar · máx 5 MB · 50 filas')}</p>
              {file && (
                <Badge className="bg-[#D4AF37] text-[#0B192C]" data-testid="bulk-selected-file">{file.name}</Badge>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                data-testid="bulk-file-input"
              />
            </CardContent>
          </Card>

          {/* Google Sheets */}
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-2 text-[#0B192C]">
                <Link2 className="w-5 h-5 text-[#D4AF37]" />
                <p className="font-semibold">{tt('Or paste a Google Sheets URL', 'O pega una URL de Google Sheets')}</p>
              </div>
              <p className="text-xs text-slate-500">{tt('Sheet must be set to "Anyone with link can view"', 'La hoja debe estar como "Cualquier persona con el enlace puede ver"')}</p>
              <Input
                value={gsUrl}
                onChange={(e) => setGsUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                data-testid="bulk-gsheet-input"
              />
              <Button
                onClick={handleGsheetPreview}
                disabled={previewing || !gsUrl}
                className="w-full bg-[#0B192C] hover:bg-[#1c2d4a] text-white"
                data-testid="bulk-gsheet-preview-btn"
              >
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : tt('Preview from Sheet', 'Vista previa desde Hoja')}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview button (after file selected) */}
      {file && !preview && !commitResult && (
        <div className="flex justify-end gap-3">
          <Button onClick={() => { setFile(null); fileInputRef.current && (fileInputRef.current.value = ''); }} variant="outline" data-testid="bulk-clear-file-btn">
            {tt('Clear', 'Borrar')}
          </Button>
          <Button
            onClick={handlePreview}
            disabled={previewing}
            className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] min-w-[160px]"
            data-testid="bulk-preview-btn"
          >
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : tt('Preview rows →', 'Vista previa →')}
          </Button>
        </div>
      )}

      {/* Summary strip — preview or commit result */}
      {(preview || commitResult) && (
        <SummaryStrip data={commitResult || preview} tt={tt} isCommit={!!commitResult} />
      )}

      {/* Action bar */}
      {(preview || commitResult) && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 border rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowOnlyErrors(v => !v)}
              variant="outline" size="sm"
              data-testid="bulk-toggle-errors-btn"
            >
              {showOnlyErrors ? tt('Show all', 'Mostrar todas') : tt('Show only errors', 'Solo errores')}
            </Button>
            <Button
              onClick={() => { setFile(null); setPreview(null); setCommitResult(null); setGsUrl(''); }}
              variant="outline" size="sm"
              data-testid="bulk-reset-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />{tt('Start over', 'Reiniciar')}
            </Button>
          </div>
          {preview && !commitResult && (
            <Button
              onClick={handleCommit}
              disabled={committing || preview.valid === 0}
              className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]"
              data-testid="bulk-commit-btn"
            >
              {committing
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : tt(`Commit ${preview.valid} valid rows →`, `Confirmar ${preview.valid} válidas →`)}
            </Button>
          )}
        </div>
      )}

      {/* Rows table */}
      {displayRows.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="bulk-rows-table">
              <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left w-12">#</th>
                  <th className="px-3 py-2 text-left">{tt('Title', 'Título')}</th>
                  <th className="px-3 py-2 text-left">{tt('City', 'Ciudad')}</th>
                  <th className="px-3 py-2 text-left">{tt('Type', 'Tipo')}</th>
                  <th className="px-3 py-2 text-right">{tt('Price', 'Precio')}</th>
                  <th className="px-3 py-2 text-left">{tt('Reference', 'Referencia')}</th>
                  <th className="px-3 py-2 text-center">{tt('Status', 'Estado')}</th>
                  {commitResult && <th className="px-3 py-2 text-center">{tt('WakaCode', 'WakaCódigo')}</th>}
                  {commitResult && <th className="px-3 py-2 text-center">{tt('Photos', 'Fotos')}</th>}
                </tr>
              </thead>
              <tbody>
                {displayRows.map(r => (
                  <RowItem
                    key={`${r.row}-${r.id || ''}`}
                    row={r}
                    tt={tt}
                    isCommit={!!commitResult}
                    onPhotoUpload={handlePhotoUpload}
                  />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Recent jobs (history) */}
      {jobs.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <h2 className="font-heading text-lg text-[#0B192C] flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-[#D4AF37]" />{tt('Recent Uploads', 'Cargas Recientes')}
            </h2>
            <div className="space-y-2" data-testid="bulk-jobs-list">
              {jobs.map(j => (
                <div key={j.id} className="flex items-center justify-between p-2 border-b last:border-0 text-sm">
                  <div>
                    <p className="font-medium text-[#0B192C]">{j.filename || tt('(no filename)', '(sin archivo)')}</p>
                    <p className="text-xs text-slate-500">{new Date(j.started_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700">+{j.created} {tt('new', 'nuevas')}</Badge>
                    {j.updated > 0 && <Badge className="bg-blue-100 text-blue-700">~{j.updated} {tt('updated', 'actualizadas')}</Badge>}
                    {j.skipped_invalid > 0 && <Badge className="bg-amber-100 text-amber-700">{j.skipped_invalid} {tt('skipped', 'omitidas')}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== Subcomponents =====
function SummaryStrip({ data, tt, isCommit }) {
  const stats = isCommit
    ? [
        { label: tt('Total rows', 'Filas totales'), value: data.total, color: 'text-slate-700' },
        { label: tt('Created', 'Creadas'), value: data.created, color: 'text-emerald-600' },
        { label: tt('Updated', 'Actualizadas'), value: data.updated, color: 'text-blue-600' },
        { label: tt('Skipped', 'Omitidas'), value: data.skipped_invalid + (data.skipped_limit || 0), color: 'text-amber-600' },
      ]
    : [
        { label: tt('Total rows', 'Filas totales'), value: data.total, color: 'text-slate-700' },
        { label: tt('Valid', 'Válidas'), value: data.valid, color: 'text-emerald-600' },
        { label: tt('With errors', 'Con errores'), value: data.errors, color: 'text-red-600' },
        { label: tt('Max rows', 'Máx filas'), value: data.max_rows || 50, color: 'text-slate-400' },
      ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bulk-summary">
      {stats.map(s => (
        <Card key={s.label} className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">{s.label}</p>
            <p className={`font-heading text-3xl font-bold ${s.color}`}>{s.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RowItem({ row, tt, isCommit, onPhotoUpload }) {
  const photoInputRef = useRef(null);
  const d = row.data || {};
  const okBg = row.ok ? 'bg-white' : 'bg-red-50/50';
  return (
    <>
      <tr className={`border-b ${okBg}`} data-testid={`bulk-row-${row.row}`}>
        <td className="px-3 py-2 text-slate-400 align-top">{row.row}</td>
        <td className="px-3 py-2 align-top max-w-[280px]">
          <div className="font-medium text-[#0B192C] truncate">{d.title || '—'}</div>
          {d.description && <div className="text-xs text-slate-500 truncate">{d.description}</div>}
        </td>
        <td className="px-3 py-2 align-top">{d.city || '—'}</td>
        <td className="px-3 py-2 align-top capitalize">{d.property_type || '—'}</td>
        <td className="px-3 py-2 align-top text-right whitespace-nowrap">{d.price ? `${d.currency || 'USD'} ${Number(d.price).toLocaleString()}` : '—'}</td>
        <td className="px-3 py-2 align-top font-mono text-xs">{d.reference || '—'}</td>
        <td className="px-3 py-2 align-top text-center">
          {row.ok ? (
            <Badge className="bg-emerald-100 text-emerald-700 gap-1" data-testid={`bulk-row-${row.row}-ok`}>
              <CheckCircle2 className="w-3 h-3" />
              {row.action === 'updated' ? tt('Updated', 'Actualizada') : (isCommit ? tt('Created', 'Creada') : tt('Valid', 'Válida'))}
            </Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700 gap-1" data-testid={`bulk-row-${row.row}-err`}>
              <AlertTriangle className="w-3 h-3" />{tt('Error', 'Error')}
            </Badge>
          )}
        </td>
        {isCommit && (
          <td className="px-3 py-2 align-top text-center font-mono text-xs">
            {row.wakacode ? <span className="text-[#D4AF37]">{row.wakacode}</span> : '—'}
          </td>
        )}
        {isCommit && (
          <td className="px-3 py-2 align-top text-center">
            {row.id ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 gap-1"
                  onClick={() => photoInputRef.current?.click()}
                  data-testid={`bulk-row-${row.row}-photo-btn`}
                >
                  <Camera className="w-3 h-3" />
                  {row.photos_uploaded
                    ? `✓ ${row.photos_count || ''}`
                    : tt('Upload', 'Subir')}
                </Button>
                <input
                  ref={photoInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onPhotoUpload(row.id, e.target.files)}
                />
              </>
            ) : '—'}
          </td>
        )}
      </tr>
      {!row.ok && row.errors && row.errors.length > 0 && (
        <tr className="bg-red-50/30 border-b">
          <td colSpan={isCommit ? 9 : 7} className="px-3 py-1">
            <div className="flex flex-wrap gap-2 pl-12">
              {row.errors.map((e, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded">
                  <X className="w-3 h-3" />
                  <span className="font-semibold">{e.field}:</span> {e.message}
                </span>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
